import { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar, SidebarProps } from './Sidebar';
import { TopBar, TopBarProps } from './TopBar';
import { useIsCompact } from '../lib/useMediaQuery';

/**
 * AppLayout — the standard authenticated-app frame.
 *
 * Composed pattern (not a single M3 component) per Agent Protocol step 2:
 *   - M3 lays out apps as Navigation Drawer + Top App Bar + content
 *     region (https://m3.material.io/foundations/layout/canonical-layouts/overview)
 *   - Populus follows the canonical pattern with CSS Grid (vs M3's
 *     implied flexbox)
 *
 * Populus adaptations applied:
 *   - CSS Grid template areas: 'sidebar topbar' / 'sidebar content'
 *     (vs M3 row+column flex; grid is cleaner for the persistent
 *     two-axis layout)
 *   - Sidebar persistent on desktop ≥1024px; below that (`useIsCompact`) it
 *     collapses to an off-canvas drawer opened from a TopBar menu button,
 *     with a click-to-dismiss backdrop and close-on-navigate
 *   - Content region scrolls independently of the Sidebar (sticky)
 *     and TopBar (sticky) — operator can scroll a long list without
 *     losing nav or breadcrumbs
 *   - Max content width 1280px (M3 implies; Populus locks for
 *     scanability — wider rows become harder to read)
 *
 * Token discipline:
 *   - Content background → --color-bg (system; warm off-white)
 *   - Sidebar surface (in child component) → --color-sidebar (system)
 *   - TopBar surface (in child component) → --color-card (system)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

/** The skip link's target: the page's main landmark. */
export const MAIN_CONTENT_ID = 'main-content';

export interface AppLayoutProps {
  sidebar: SidebarProps;
  topbar?: TopBarProps;
  children: ReactNode;
  contentMaxWidth?: number;
}

export function AppLayout({
  sidebar,
  topbar,
  children,
  contentMaxWidth = 1280,
}: AppLayoutProps) {
  const compact = useIsCompact();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the drawer on navigation, and whenever the viewport grows back to
  // desktop (so it can't linger open once the sidebar is persistent again).
  useEffect(() => { setDrawerOpen(false); }, [pathname]);
  useEffect(() => { if (!compact) setDrawerOpen(false); }, [compact]);

  return (
    <div style={{
      display: 'grid',
      gridTemplateAreas: compact
        ? '"topbar" "content"'
        : '"sidebar topbar" "sidebar content"',
      gridTemplateColumns: compact ? '1fr' : '240px 1fr',
      gridTemplateRows: '56px 1fr',
      height: '100vh',
      overflow: 'hidden',
      background: 'var(--color-bg)',
    }}>
      {/* Skip link: the first tab stop on every page, so a keyboard user can jump
          past the sidebar and top bar straight to the page content. Hidden until
          focused (global.css .skip-link). Focus is moved in script rather than by
          following the #fragment, so the router's URL is left alone. */}
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById(MAIN_CONTENT_ID)?.focus();
        }}
      >
        Skip to main content
      </a>
      <Sidebar
        {...sidebar}
        compact={compact}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
      {compact && drawerOpen && (
        <div
          aria-hidden="true"
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 20,
            background: 'rgba(15, 23, 24, 0.5)',
          }}
        />
      )}
      {(topbar || compact) && (
        <TopBar
          {...(topbar ?? {})}
          onMenuClick={compact ? () => setDrawerOpen(true) : undefined}
        />
      )}
      <main id={MAIN_CONTENT_ID} tabIndex={-1} style={{
        gridArea: 'content',
        padding: compact ? 'var(--sp-5) var(--sp-4)' : 'var(--sp-6) var(--sp-7)',
        overflowY: 'auto',
        overflowX: 'auto',
        minWidth: 0,
        minHeight: 0,
      }}>
        <div style={{ maxWidth: contentMaxWidth, margin: '0 auto' }}>
          {children}
        </div>
      </main>
    </div>
  );
}
